import { useState, useEffect } from 'react';
import { Siren, Ambulance, Building2, Clipboard, Activity, Award, Clock, FileText, AlertTriangle, Share2, Printer } from 'lucide-react';
import { T } from '@design/index';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { PatientCertificateDoc, TimelineEvent } from '../types';
import NHCIdBadge from '../components/NHCIdBadge';
import NeoVerifyBadge from '../components/NeoVerifyBadge';
import Spinner from '../../../components/ui/Spinner';

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtDateTime(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function EmergencyRecords() {
  const [ambulanceCerts, setAmbulanceCerts] = useState<PatientCertificateDoc[]>([]);
  const [emergencyCerts, setEmergencyCerts] = useState<PatientCertificateDoc[]>([]);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [nhcId, setNhcId] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      patientApi.getCertificates({ type: 'AMBULANCE_TRANSPORT_CERTIFICATE' }),
      patientApi.getCertificates({ type: 'EMERGENCY_EVENT_CERTIFICATE' }),
      patientApi.getTimeline({ type: 'EMERGENCY', limit: 10 }),
      patientApi.getProfile(),
    ]).then(([amb, emg, tl, prof]) => {
      if (alive) { setAmbulanceCerts(amb); setEmergencyCerts(emg); setTimelineEvents(tl.events); setNhcId(prof.nhcId); }
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const allCerts = [...ambulanceCerts, ...emergencyCerts];

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" style={govPageContainer()}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2" style={GOV_TYPE.pageTitle}>
            <Siren className="w-6 h-6 text-red-600" /> Emergency Records
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">ECC-linked emergency events, ambulance records & certificates</p>
        </div>
        {nhcId && <NHCIdBadge nhcId={nhcId} />}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="card p-4 text-center">
          <Ambulance className="w-6 h-6 text-orange-500 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{ambulanceCerts.length}</p>
          <p className="text-[10px] text-gray-500">Ambulance Records</p>
        </div>
        <div className="card p-4 text-center">
          <Siren className="w-6 h-6 text-red-500 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{emergencyCerts.length}</p>
          <p className="text-[10px] text-gray-500">Emergency Events</p>
        </div>
        <div className="card p-4 text-center">
          <Award className="w-6 h-6 text-teal-500 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{allCerts.filter(c => c.verificationStatus === 'VERIFIED').length}</p>
          <p className="text-[10px] text-gray-500">NeoVerified</p>
        </div>
      </div>

      {/* Emergency Certificates */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-3">Emergency & Ambulance Certificates</h2>
        {allCerts.length === 0 ? (
          <div className="card p-8 text-center">
            <Siren className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No emergency records</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allCerts.map(c => (
              <div key={c.id} className="card p-4 border-l-4 border-l-red-400">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">{c.title}</h3>
                    {c.description && <p className="text-xs text-gray-500 mt-1">{c.description}</p>}
                    <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-500">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDateTime(c.issueDate)}</span>
                      {c.issuerName && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {c.issuerName}</span>}
                      {/* `linkedModule` is not a field this API sends; the
                          certificate's own number is. */}
                      {c.certificateNumber && <span className="flex items-center gap-1 font-mono"><FileText className="w-3 h-3" /> {c.certificateNumber}</span>}
                    </div>
                  </div>
                  <NeoVerifyBadge status={c.verificationStatus} neoVerifyId={c.neoVerifyId} />
                </div>

                <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-100">
                  <button onClick={() => {
                    const text = `Emergency Record — ${c.title}\nIssued: ${fmtDateTime(c.issueDate)}\nBy: ${c.issuerName || 'Emergency Services'}\nNeoVerify: ${c.neoVerifyId || 'Pending'}\n\nVerified through NeoHealthCard`;
                    if (navigator.share) { navigator.share({ title: c.title, text }).catch(() => {}); }
                    else { navigator.clipboard.writeText(text).catch(() => {}); }
                  }} className="text-[11px] text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                    <Share2 className="w-3 h-3" /> Share
                  </button>
                  <button onClick={() => window.print()} className="text-[11px] text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1">
                    <Printer className="w-3 h-3" /> Print
                  </button>
                </div>

                {/* Placeholder details */}
                <div className="mt-3 pt-2 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    {/* Was "Event ID" read from `linkedEventId`, which this
                        API does not send — so it printed an em dash on every
                        record. The NeoVerify id is the one a responder checks. */}
                    <p className="text-[10px] text-gray-400">NeoVerify ID</p>
                    <p className="text-xs font-mono text-gray-600">{c.neoVerifyId || 'Not registered'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Booking ID</p>
                    <p className="text-xs font-mono text-gray-600">—</p>
                    <p className="text-[10px] text-gray-400">Placeholder</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Hospital ER</p>
                    <p className="text-xs font-mono text-gray-600">—</p>
                    <p className="text-[10px] text-gray-400">Placeholder</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">NeoVerify</p>
                    <p className="text-xs font-mono text-gray-600">{c.neoVerifyId ? c.neoVerifyId.substring(0, 12) : '—'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Placeholders */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
            <Clipboard className="w-4 h-4 text-gray-400" /> Paramedic Notes
          </h3>
          <p className="text-xs text-gray-400">Placeholder — paramedic notes will appear here when connected to ambulance emergency records</p>
        </div>
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-gray-400" /> Vitals
          </h3>
          <p className="text-xs text-gray-400">Placeholder — vital signs recorded during transport will appear here</p>
        </div>
      </div>

      {/* Emergency Timeline Events */}
      {timelineEvents.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-gray-900 mb-3">Emergency Timeline Events</h2>
          <div className="space-y-2">
            {timelineEvents.map(ev => (
              <div key={ev.id} className="card p-3 flex items-center gap-3">
                <Siren className="w-4 h-4 text-red-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{ev.title}</p>
                  {ev.description && <p className="text-xs text-gray-500">{ev.description}</p>}
                </div>
                <span className="text-[10px] text-gray-400 shrink-0">{fmtDate(ev.eventDate)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-gray-400 text-center">Verified through NeoHealthCard™ · Emergency access is audit logged · All records consent-controlled</p>
    </div>
  );
}
